/**
 * Favicon utilities
 *
 * Resolve favicon/icon URLs for domains or full URLs. Used by integrations,
 * ingest (website sources), file sources, and other UI that shows site icons.
 *
 * Primary responsibilities:
 * - Extract domain from URL
 * - Build Google favicon API URL for reliable brand icons
 */

const GOOGLE_FAVICON_BASE = "https://www.google.com/s2/favicons"

/** Default size for favicon (Google API: 16, 32, 48, 64, 128, 256). */
export const FAVICON_DEFAULT_SIZE = 128

/**
 * Extract hostname (domain) from a URL string.
 * Handles http(s), protocol-relative, and bare domains.
 */
export function getDomainFromUrl(url: string): string | null {
  if (!url || typeof url !== "string") return null
  const trimmed = url.trim()
  if (!trimmed) return null
  try {
    const withProtocol = trimmed.startsWith("http://") || trimmed.startsWith("https://")
      ? trimmed
      : `https://${trimmed}`
    const u = new URL(withProtocol)
    return u.hostname || null
  } catch {
    return null
  }
}

/**
 * Return Google favicon API URL for a domain or full URL.
 * Use for integrations, ingest sources, file sources, bookmarks, etc.
 *
 * @param domainOrUrl - Domain (e.g. "clio.com") or full URL (e.g. "https://clio.com/foo")
 * @param size - Pixel size; one of 16, 32, 48, 64, 128, 256 (default 128)
 * @returns URL string or null if domain could not be resolved
 */
export function getGoogleFaviconUrl(
  domainOrUrl: string,
  size: number = FAVICON_DEFAULT_SIZE
): string | null {
  const domain = domainOrUrl.includes("/") || domainOrUrl.startsWith("http")
    ? getDomainFromUrl(domainOrUrl)
    : domainOrUrl.includes(".")
      ? domainOrUrl.trim()
      : null
  if (!domain) return null
  const sz = [16, 32, 48, 64, 128, 256].includes(size) ? size : FAVICON_DEFAULT_SIZE
  return `${GOOGLE_FAVICON_BASE}?domain=${encodeURIComponent(domain)}&sz=${sz}`
}
